import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AiIntentsState } from 'apps/aiIntents/interfaces/interfaces'
import { Ability } from 'common/core/constants/Ability'
import { ChannelType } from 'common/core/constants/ChannelType'
import { getAbility } from 'common/core/selectors/abilitiesSelectors'
import { TriggerType } from 'common/flow/constants/TriggerType'
import { handleError } from 'shared/api/lib/errors/handlers'
import { AiIntentsApi } from 'shared/api/requests/aiIntents'
import { AiIntent, AiIntentStatus } from 'shared/api/requests/aiIntents/schemas'
import { makeCreateAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import { analyticsService } from 'utils/services/analytics'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_intents',
})

export const AiIntentsAdapter = createEntityAdapter<AiIntent, number>({
  selectId: (aiIntent) => aiIntent.intent_id,
})

export const InitialState: AiIntentsState = {
  list: AiIntentsAdapter.getInitialState(),
}

const AiIntentsSlice = createSlice({
  name: 'aiIntents',
  initialState: InitialState,
  reducers: {
    resetState: () => InitialState,

    removeOne: (state, action: PayloadAction<number>) => {
      AiIntentsAdapter.removeOne(state.list, action.payload)
    },

    upsertOne: (state, action: PayloadAction<AiIntent>) => {
      AiIntentsAdapter.upsertOne(state.list, action.payload)
    },
  },
})

const { upsertOne, removeOne, resetState } = AiIntentsSlice.actions

const createFx = createAsyncAction(
  'aiIntents/createFx',
  async (
    {
      intent,
      channel,
      flow_ns,
    }: {
      intent: string
      channel: ChannelType
      flow_ns: string | null
    },
    { dispatch },
  ) => {
    const {
      data: { intent_rule },
    } = await AiIntentsApi.createAiIntent({
      body: { intent, channel, flow_ns },
      query: { client_id: getClientId() },
    })

    dispatch(upsertOne(intent_rule))

    return intent_rule
  },
)

const fetchByIdFx = createAsyncAction(
  'aiIntents/fetchByIdFx',
  async (intent_id: number, { dispatch }) => {
    const {
      data: { intent_rule },
    } = await AiIntentsApi.getAiIntent({
      query: { intent_id, client_id: getClientId() },
    })

    dispatch(upsertOne(intent_rule))

    return intent_rule
  },
)

const removeFx = createAsyncAction(
  'aiIntents/removeFx',
  async (intent_id: number, { dispatch }) => {
    await AiIntentsApi.deleteAiIntent({
      body: { intent_id },
      query: { client_id: getClientId() },
    })

    dispatch(removeOne(intent_id))

    return intent_id
  },
)

const updateStatusFx = createAsyncAction(
  'aiIntents/updateStatusFx',
  async (
    {
      intent_id,
      status,
      doNotLogStatusChange,
    }: {
      intent_id: number
      status: AiIntentStatus
      doNotLogStatusChange?: boolean
      silent?: boolean
    },
    { dispatch },
  ) => {
    const {
      data: { intent_rule },
    } = await AiIntentsApi.updateAiIntentStatus({
      body: { intent_id, status },
      query: { client_id: getClientId() },
    })

    if (!doNotLogStatusChange) {
      analyticsService.sendEvent('TRIGGER.STATUS.SWITCHED', {
        isEnabled: status === AiIntentStatus.ACTIVE,
        triggerId: `${TriggerType.AI_INTENT}-${intent_id}`,
      })
    }

    dispatch(upsertOne(intent_rule))

    return intent_rule
  },
  {
    condition(_, { getState }) {
      return getAbility(getState(), Ability.AI_KEYWORD).isEnabled
    },
    onError(error, payload) {
      if (!payload.silent) {
        handleError(error, {
          scope: 'ai',
          section: 'ai_intents',
        })
      }

      return true
    },
  },
)

const updateFx = createAsyncAction(
  'aiIntents/updateFx',
  async (
    { intentId, intent, flowId }: { intentId: number; intent: string; flowId?: string },
    { dispatch },
  ) => {
    const {
      data: { intent_rule },
    } = await AiIntentsApi.updateAiIntent({
      body: { intent_id: intentId, intent, flow_ns: flowId },
      query: { client_id: getClientId() },
    })

    dispatch(upsertOne(intent_rule))

    return intent_rule
  },
)

export const AiIntentsReducer = AiIntentsSlice.reducer

export const AiIntentsSelectors = {
  getById: (state: RootState, id: number) =>
    AiIntentsAdapter.getSelectors().selectById(state.aiIntents.list, id),
}

export const AiIntentsActions = {
  fetchByIdFx,
  createFx,
  removeFx,
  updateStatusFx,
  updateFx,
  resetState,
}
